import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, input, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SingleSelectComponent } from '@examdojo/core/select';
import { IconComponent } from '@examdojo/core/icon';
import {
  STYLE_SCHEMAS,
  type StyleKeyType,
  type StylePropertySchema,
} from '../style-editor/style-property-schema.model';
import { StylePropertyEditorComponent } from '../style-editor/style-property-editor.component';

interface SelectOption {
  label: string;
  value: string;
}

export interface PatchDisplayTarget {
  elementId: string;
  styles: Record<string, Record<string, unknown>>;
  visible?: boolean;
}

const ALL_STYLE_KEYS: StyleKeyType[] = ['point', 'line', 'curve', 'polygon', 'angle', 'circle'];

@Component({
  selector: 'dojo-patch-display-target-editor',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatSlideToggleModule,
    SingleSelectComponent,
    IconComponent,
    StylePropertyEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <details class="group rounded-md bg-sky-50 p-2">
      <summary
        class="flex cursor-pointer select-none items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-neutral-400"
      >
        <span class="transition-transform group-open:rotate-90">&#9654;</span>
        <span class="flex-1 truncate">{{ target().elementId || 'New Target' }}</span>
        <dojo-icon
          icon="xmark"
          [size]="10"
          class="cursor-pointer text-neutral-400 hover:text-red-500"
          (click)="remove.emit(); $event.stopPropagation()"
        />
      </summary>

      <div class="mt-1.5 flex flex-col gap-1.5">
        <!-- Element ID selector -->
        <dojo-single-select
          label="Element"
          [formCtrl]="elementIdCtrl"
          [options]="elementIdOptions()"
          optionLabel="label"
          optionValue="value"
          [size]="'s'"
          placeholder="Element ID..."
          (selectionChange)="onElementIdChange($event)"
        />

        <!-- Active style key sections -->
        @for (styleKey of activeStyleKeys(); track styleKey) {
          <div class="rounded bg-white/60 px-2 py-1.5">
            <div class="mb-1 flex items-center gap-1">
              <span class="text-[9px] font-semibold uppercase tracking-wider text-neutral-400">{{
                getStyleLabel(styleKey)
              }}</span>
              <dojo-icon
                icon="xmark"
                [size]="9"
                class="ml-auto cursor-pointer text-neutral-300 hover:text-red-500"
                (click)="onRemoveStyleKey(styleKey)"
              />
            </div>
            <div class="grid grid-cols-2 gap-1.5">
              @for (prop of getPropertiesForKey(styleKey); track prop.key) {
                <dojo-style-property-editor
                  [schema]="prop"
                  [value]="getStyleValue(styleKey, prop.key)"
                  (valueChange)="onStylePropertyChange(styleKey, prop.key, $event)"
                />
              }
            </div>
          </div>
        }

        <!-- Add style key + visible toggle row -->
        <div class="flex items-center gap-1.5">
          @if (availableStyleKeys().length) {
            <dojo-single-select
              [formCtrl]="addStyleKeyCtrl"
              [options]="availableStyleKeyOptions()"
              optionLabel="label"
              optionValue="value"
              [size]="'s'"
              placeholder="+ Add style..."
              class="flex-1"
              (selectionChange)="onAddStyleKey($event)"
            />
          }
          <mat-slide-toggle class="scale-75" [checked]="isVisible()" (change)="onVisibleChange($event.checked)"
            >Visible</mat-slide-toggle
          >
        </div>
      </div>
    </details>
  `,
})
export class PatchDisplayTargetEditorComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    effect(() => {
      this.target();
      this.syncFromTarget();
    });
  }

  readonly target = input.required<PatchDisplayTarget>();
  readonly elementIds = input<string[]>([]);

  readonly targetChange = output<PatchDisplayTarget>();
  readonly remove = output<void>();

  readonly elementIdCtrl = new FormControl<string>('', { nonNullable: true });
  readonly addStyleKeyCtrl = new FormControl<string>('', { nonNullable: true });

  readonly elementIdOptions = computed<SelectOption[]>(() => this.elementIds().map((id) => ({ label: id, value: id })));

  readonly activeStyleKeys = computed<StyleKeyType[]>(() => {
    const styles = this.target().styles;
    return Object.keys(styles).filter((k) => k in STYLE_SCHEMAS) as StyleKeyType[];
  });

  readonly availableStyleKeys = computed<StyleKeyType[]>(() => {
    const active = new Set(this.activeStyleKeys());
    return ALL_STYLE_KEYS.filter((k) => !active.has(k));
  });

  readonly availableStyleKeyOptions = computed<SelectOption[]>(() =>
    this.availableStyleKeys().map((k) => ({ label: STYLE_SCHEMAS[k].label, value: k })),
  );

  readonly isVisible = computed(() => this.target().visible !== false);

  getStyleLabel(key: StyleKeyType): string {
    return STYLE_SCHEMAS[key]?.label ?? key;
  }

  getPropertiesForKey(key: StyleKeyType): readonly StylePropertySchema[] {
    return STYLE_SCHEMAS[key]?.properties ?? [];
  }

  getStyleValue(styleKey: string, propKey: string): unknown {
    return this.target().styles[styleKey]?.[propKey];
  }

  onElementIdChange(value: unknown): void {
    const elementId = (value as string) || '';
    this.targetChange.emit({ ...this.target(), elementId });
  }

  onStylePropertyChange(styleKey: string, propKey: string, value: unknown): void {
    const current = this.target();
    const currentStyle = current.styles[styleKey] ?? {};
    const updatedStyle = { ...currentStyle, [propKey]: value };

    // Remove undefined values
    if (value === undefined || value === null || value === '') {
      delete updatedStyle[propKey];
    }

    const styles = { ...current.styles, [styleKey]: updatedStyle };

    // Remove empty style keys
    if (Object.keys(updatedStyle).length === 0) {
      delete styles[styleKey];
    }

    this.targetChange.emit({ ...current, styles });
  }

  onAddStyleKey(value: unknown): void {
    const key = value as string;
    if (!key) {
      return;
    }
    const current = this.target();
    this.targetChange.emit({
      ...current,
      styles: { ...current.styles, [key]: {} },
    });
    this.addStyleKeyCtrl.setValue('', { emitEvent: false });
  }

  onRemoveStyleKey(key: string): void {
    const current = this.target();
    const { [key]: _, ...remaining } = current.styles;
    this.targetChange.emit({ ...current, styles: remaining });
  }

  onVisibleChange(visible: boolean): void {
    const current = this.target();
    this.targetChange.emit({ ...current, visible: visible ? undefined : false });
  }

  private syncFromTarget(): void {
    const t = this.target();
    this.elementIdCtrl.setValue(t.elementId ?? '', { emitEvent: false });
  }
}
